import { Toaster } from "react-hot-toast";
import { SWRConfig } from "swr";
import { Route, Routes, useLocation } from "react-router-dom";
import { RequireAuth } from "./require-auth";
import { AuthenticatedApp } from "./components/dashboard/authenticated_app";
import ForgotPasswordPage from "./modules/authentication/presentation/pages/forgot_password_page";
import { CreateAccountPage } from "./modules/authentication/presentation/pages/create_account_page";
import CredentialsStep from "./modules/authentication/presentation/components/register/wizard/credentials-step";
import PartnerTypeStep from "./modules/authentication/presentation/components/register/wizard/partner-type-step";
import BusinessDetailsStep from "./modules/authentication/presentation/components/register/wizard/business-details-step";
import VerifyEmailStep from "./modules/authentication/presentation/components/register/wizard/verify-email-step";
import VerifyPhoneStep from "./modules/authentication/presentation/components/register/wizard/verify-phone-step";
import DocumentsStep from "./modules/authentication/presentation/components/register/wizard/documents-step";
import BankingDetailsStep from "./modules/authentication/presentation/components/register/wizard/banking-details-step";
import ApprovalPendingStep from "./modules/authentication/presentation/components/register/wizard/approval-pending-step";
import { useMe } from "./swr/useMe";
import { HomePageSkeleton } from "./components/common/home-page-skeleton";

import { footerRoutesBuilder } from "./common/routes/footer_routes";
import ResetPasswordPage from "./modules/authentication/presentation/pages/reset_password_page";
import VerifyResetOtpPage from "./modules/authentication/presentation/pages/verify_reset_otp_page";
import { AppRoutes } from "./common/routes/app_routes";
import { useRedirects } from "./common/routes/redirect_handlers";
import { authRoutes } from "./common/routes/route_utils";
import { useCallback, useEffect } from "react";
import { useReferralStore } from "./modules/authentication/domain/stores/useReferralStore";
import { useRegisterWizard } from "./modules/authentication/domain/register-wizard";
import { usePublicChatDraft } from "./modules/ai-search/domain/public-chat-draft-store";
import { useUserCartSync } from "./hooks/useUserCartSync";

import type { UserDto } from "./api";
import LoginPage from "./modules/authentication/presentation/pages/login-page";
import LandingPage from "./modules/landing/presentation/pages/landing-page";
import { useChat } from "./modules/ai-search/hooks/useChat";
import { HandpickedDealsSection } from "./modules/featured-deals/presentation/components/handpicked-deals-section";

function PublicLandingPage() {
  const { messages, responding, sendMessage, retryLastMessage, stopGenerating } = useChat({
    persistGuestDraft: true,
  });
  const handleSearch = useCallback((query: string) => {
    void sendMessage(query);
  }, [sendMessage]);
  // Widget callback signature carries an optional productId (e.g. "Chat
  // about <product>") that doesn't line up positionally with sendMessage's
  // own (text, attachments?, productId?) signature.
  const handleWidgetSendMessage = useCallback((text: string, productId?: number) => {
    void sendMessage(text, undefined, productId);
  }, [sendMessage]);
  return (
    <LandingPage
      onSearch={handleSearch}
      chatTranscript={messages}
      chatResponding={responding}
      chatRetry={retryLastMessage}
      chatStopGenerating={stopGenerating}
      chatSendMessage={handleWidgetSendMessage}
      // Restores the results-panel view when this page remounts with a
      // non-empty draft — e.g. a logged-out user bounced to /login by a
      // guarded action (like "Compare") who then hits browser Back.
      restoreResultsOpen={messages.length > 0}
      afterHero={<HandpickedDealsSection />}
    />
  );
}

function RedirectHandler({
  user,
  isLoading,
}: {
  user?: UserDto;
  isLoading: boolean;
}) {
  const redirect = useRedirects(useLocation(), user);
  if (redirect) return redirect;

  return (
    <RequireAuth user={user} isLoading={isLoading}>
      <AuthenticatedApp />
    </RequireAuth>
  );
}

function App() {
  const { user, isLoading } = useMe();
  const location = useLocation();

  const { initializeReferralId } = useReferralStore();

  // Sync user ID to localStorage and handle cart migration when user changes
  useUserCartSync(user);

  useEffect(() => {
    initializeReferralId();
  }, [initializeReferralId]);

  // Wizard state is only meaningful inside /create-account/*. As soon as the
  // user navigates anywhere else (login, landing, etc.), drop it so a stale
  // half-filled signup doesn't leak into a fresh attempt.
  useEffect(() => {
    if (location.pathname.startsWith(AppRoutes.CREATE_ACCOUNT)) return;
    const s = useRegisterWizard.getState();
    if (s.email || s.fullName || s.password || s.registered || s.businessName) {
      s.reset();
    }
  }, [location.pathname]);

  // Once authenticated, the logged-out AI search draft is stale — drop it so
  // a later logout back to `/` in the same tab doesn't resurface someone
  // else's leftover search.
  useEffect(() => {
    if (user) usePublicChatDraft.getState().clearDraft();
  }, [user]);

  if (isLoading) {
    return <HomePageSkeleton />;
  }

  return (
    <SWRConfig
      value={{
        refreshInterval: 0,
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      }}
    >
      <div
        className="fixed left-1/2 -translate-x-1/2 pointer-events-none w-full"
        data-fixed-safe-y
        style={{ zIndex: 9999 }}
      >
        <Toaster
          toastOptions={{
            style: {
              fontSize: 14,
              margin: "0 auto",
              cursor: "pointer",
            },
            duration: 5000,
          }}
          containerStyle={{
            position: "relative",
            left: 0,
            right: 0,
          }}
        />
      </div>

      <Routes>
        <Route path={AppRoutes.LOGIN} element={<LoginPage />} />
        <Route
          path={AppRoutes.FORGOT_PASSWORD}
          element={<ForgotPasswordPage />}
        />
        <Route
          path={AppRoutes.RESET_PASSWORD_VERIFY}
          element={<VerifyResetOtpPage />}
        />
        <Route
          path={AppRoutes.RESET_PASSWORD}
          element={<ResetPasswordPage />}
        />
        <Route path={AppRoutes.CREATE_ACCOUNT} element={<CreateAccountPage />}>
          <Route index element={<CredentialsStep />} />
          <Route path="partner-type" element={<PartnerTypeStep />} />
          <Route path="business-details" element={<BusinessDetailsStep />} />
          <Route path="verify-email" element={<VerifyEmailStep />} />
          <Route path="verify-phone" element={<VerifyPhoneStep />} />
          <Route path="documents" element={<DocumentsStep />} />
          <Route path="banking-details" element={<BankingDetailsStep />} />
          <Route path="approval-pending" element={<ApprovalPendingStep />} />
        </Route>

        {/* Footer routes are accessible without login */}
        {footerRoutesBuilder()}

        {authRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <RequireAuth user={user} isLoading={isLoading}>
                {route.element}
              </RequireAuth>
            }
          />
        ))}

        {!user && (
          <Route path={AppRoutes.INDEX} element={<PublicLandingPage />} />
        )}
        <Route
          path="/*"
          element={<RedirectHandler user={user} isLoading={isLoading} />}
        />
      </Routes>
    </SWRConfig>
  );
}

export default App;
